import {
  List as IList,
  Set as ISet,
} from 'immutable';
import {
  Component,
  For,
  JSXElement,
  Show,
  createMemo,
  createSignal,
  useContext,
} from 'solid-js';
import {
  ArchiveChunksIndex,
  Button,
  ChunkingAlgorithm,
  CompressionAlgorithm,
  ContainerEntry,
  DialogParams,
  DirectoryEntry,
  Entry,
  EntryContextProvider,
  EntryUploadExtra,
  FaIcon,
  FileEntry,
  FileEntryMode,
  LocalizationContext,
  PrettyDataSize,
  Tags,
  TagsEditor,
  UploadBox,
  calculateArchiveChunksIndex,
  compareTags,
} from '../components';
import { ProjectInfo, ProjectQuotasInfo } from '../components/api-generated';

export type NewPackage = {
  title: string;
  tags: ISet<string>;
  entrypoint: IList<string>;
  root: DirectoryEntry;
  chunkingAlgorithm: ChunkingAlgorithm;
  compressionAlgorithm: CompressionAlgorithm;
  baseChunksIndex?: ArchiveChunksIndex;
};

export const NewPackageDialog: Component<DialogParams<NewPackage> & {
  project: ProjectInfo;
  projectQuotas: ProjectQuotasInfo;
  version?: string;
  baseRoot?: DirectoryEntry;
  cloneFilesFromBase?: boolean;
  tags?: ISet<string>;
  entrypoint?: IList<string>;
}> = (props) => {
  const { t } = useContext(LocalizationContext)!;
  const [tags, setTags] = createSignal(props.tags ?? ISet<string>());

  const root = (props.baseRoot && props.cloneFilesFromBase) ? props.baseRoot.clone() : new DirectoryEntry('');

  const [entrypoint, setEntrypoint] = createSignal<IList<string>>(props.entrypoint ?? IList());

  let refTitle: HTMLInputElement | undefined;

  const tagsStr = createMemo<string>((lastTagsStr) => {
    const tagsStr = (props.version ? [props.version] : []).concat(tags().toArray().sort(compareTags).map((tag) => tag.split(':').at(-1)!)).join('-');
    if(refTitle?.value === '' || refTitle?.value === lastTagsStr) {
      refTitle!.value = tagsStr;
    }
    return tagsStr;
  }, '');

  const onTitleBlur = (e: FocusEvent) => {
    if(refTitle?.value === '') {
      refTitle!.value = tagsStr();
    }
  };

  type Check = {
    message: JSXElement;
    action?: {
      title: JSXElement;
      fix: () => void;
    };
  };

  const checkEmpty = createMemo<Check | null>(() => {
    if(root.entries().isEmpty()) {
      return {
        message: t('packages.new_dialog.checks.empty'),
      };
    }

    return null;
  });

  const checkEntrypoint = createMemo<Check | null>(() => {
    const entrypointValue = entrypoint();

    // check if entrypoint is not set
    if(entrypointValue.isEmpty()) {
      return {
        message: t('packages.new_dialog.checks.entrypoint.empty'),
      }
    }

    // check if entrypoint is set, but entry is missing
    let entry: Entry | undefined = root;
    for(let i = 0; entry; ++i) {
      const name = entrypointValue.get(i);
      if(!name) break;
      entry = entry instanceof ContainerEntry ? entry.entries().get(name) : undefined;
    }
    if(!entry) {
      return {
        message: t('packages.new_dialog.checks.entrypoint.missing')(<code>{entrypointValue.toArray().join('/')}</code>),
        action: {
          title: t('packages.new_dialog.checks.entrypoint.missing.action'),
          fix: () => {
            setEntrypoint(IList([]));
          },
        },
      }
    }

    // check type of entrypoint file and suggest tags or wrong files
    const entryName = entry.name();
    const tagsSet = tags();
    for(const [fileExt, tag, category] of [
      ['7z', undefined, 'archive'],
      ['bat', 'os:windows'],
      ['css'],
      ['dat'],
      ['deb', 'packaging:deb'],
      ['dll'],
      ['exe', 'os:windows'],
      ['flatpak', 'packaging:flatpak'],
      ['html', 'os:web'],
      ['jar', 'runtime:java'],
      ['js', 'runtime:nodejs'],
      ['json'],
      ['msi', 'packaging:installer'],
      ['rar', undefined, 'archive'],
      ['rpm', 'packaging:rpm'],
      ['sh', 'os:linux'],
      ['so'],
      ['tar.bz', undefined, 'archive'],
      ['tar.gz', undefined, 'archive'],
      ['tar.xz', undefined, 'archive'],
      ['tar', undefined, 'archive'],
      ['toml'],
      ['txt'],
      ['yaml'],
      ['yml'],
      ['zip', undefined, 'archive'],
    ] as ([string] | [string, string] | [string, string | undefined, string])[]) {
      if(entryName.endsWith(`.${fileExt}`) && (!tag || !tagsSet.has(tag))) {
        return {
          message: (
            t(`packages.new_dialog.checks.entrypoint.ext.${fileExt}`) ??
            t(`packages.new_dialog.checks.entrypoint.ext${category ? `.${category}` : (tag ? '.tag' : '.wrong')}`)
          )(
            <code>.{fileExt}</code>,
            tag ? <Tags tags={ISet([tag])} inline /> : undefined
          ),
          action: tag ? {
            title: t('packages.new_dialog.checks.entrypoint.ext.tag.action')(t(`package.tag.${tag}`)),
            fix: () => {
              setTags((tags) => tags.add(tag));
            },
          } : undefined,
        };
      }
    }

    // advise to add executable bit
    if((
      tagsSet.has('os:linux') ||
      tagsSet.has('os:macos')
    ) && entry instanceof FileEntry && !(entry.mode() & FileEntryMode.Executable) && [
      'deb',
      'flatpak',
      'rpm',
    ].every((ext) => !entry.name().endsWith(`.${ext}`))) {
      return {
        message: t('packages.new_dialog.checks.entrypoint.executable'),
        action: {
          title: t('packages.new_dialog.checks.entrypoint.executable.action'),
          fix: () => {
            entry.setMode((mode) => mode | FileEntryMode.Executable);
          },
        },
      };
    }

    return null;
  });

  const checkSingleRootDir = createMemo<Check | null>(() => {
    const entries = root.entries();
    if(entries.size == 1 && entries.first()! instanceof DirectoryEntry) {
      return {
        message: t('packages.new_dialog.checks.single_root_dir'),
        action: {
          title: t('packages.new_dialog.checks.single_root_dir.action'),
          fix: () => {
            if(entries.size == 1) {
              const entry = entries.first();
              if(entry instanceof DirectoryEntry) {
                entry.removeFromParent();
                for(const [_subentryName, subentry] of entry.entries()) {
                  subentry.moveTo(root);
                }
              }
            }
          },
        },
      };
    } else return null;
  });

  const checks = createMemo(() => [
    checkEmpty(),
    checkEntrypoint(),
    checkSingleRootDir(),
  ].filter((check) => check != null));

  const chunkingAlgorithm = ChunkingAlgorithm.Gear17;
  const compressionAlgorithm = CompressionAlgorithm.None;
  const baseChunksIndex = props.baseRoot ? calculateArchiveChunksIndex(props.baseRoot) : undefined;

  const onStartUpload = () => {
    props.resolve({
      title: refTitle!.value,
      tags: tags(),
      entrypoint: entrypoint(),
      root,
      chunkingAlgorithm,
      compressionAlgorithm,
      baseChunksIndex,
    });
  };

  return (
    <props.dialog class="sheet ui big new_package">
      <h1>{t('packages.new_dialog.header')}</h1>
      <h2>{t('packages.new_dialog.tags.header')}</h2>
      <TagsEditor tags={tags} setTags={setTags} compact />
      <Tags tags={tags()} showCodes />
      <h2>{t('packages.new_dialog.files.header')}</h2>
      <EntryContextProvider>
        <UploadBox
          root={root}
          baseRoot={props.baseRoot}
          baseChunksIndex={baseChunksIndex}
          entrypoint={entrypoint}
          setEntrypoint={setEntrypoint}
          editable
        />
      </EntryContextProvider>
      <div class="message">
        <Show when={!entrypoint().isEmpty()}>{
          t('packages.entrypoint')(<>
            <FaIcon solid play inline weak />
            <code>{entrypoint().toArray().join('/')}</code>
          </>)
        }</Show>
      </div>
      <Show when={checks().length > 0}>
        <div class="checks">
          <For each={checks()}>{(check) =>
            <div>
              <p class="message"><FaIcon solid warning inline weak />{check?.message}</p>
              <Show when={check?.action}>
                <div class="buttons">
                  <Button onClick={check?.action?.fix}>{check?.action?.title}</Button>
                </div>
              </Show>
            </div>
          }</For>
        </div>
      </Show>
      <div class="size">{t('packages.new_dialog.total_size')(
        <span class="inline_size"><PrettyDataSize size={root.totalSize()} /></span>,
        <span class="note">{t('packages.new_dialog.total_size.maximum')(<span class="inline_size"><PrettyDataSize size={props.projectQuotas.packageSize} /></span>)}</span>
      )}</div>
      <div class="size">{t('packages.new_dialog.new_data_size')(
        <Show when={(root.extra as EntryUploadExtra).ready()} fallback={
          <span class="note">{t('calculating')}</span>
        }>
          <span class="inline_size"><PrettyDataSize size={(root.extra as EntryUploadExtra)?.patchSize?.(props.baseRoot)?.() ?? 0} /></span>
        </Show>
      )}</div>
      <label class="title">
        <div class="label">{t('packages.new_dialog.label_title')}</div>
        <div class="value"><input type="text" ref={refTitle} placeholder={tagsStr() || t('packages.new_dialog.title_placeholder')} onBlur={onTitleBlur} autofocus /></div>
      </label>
      <div class="footer">
        <div class="buttons align_end">
          <Button onClick={onStartUpload} disabled={root.entries().isEmpty() || !(root.extra as EntryUploadExtra)?.ready?.()}><FaIcon inline solid cloud-arrow-up />{t('upload_box.start_upload')}</Button>
          <Button onClick={() => props.resolve()}>{t('cancel')}</Button>
        </div>
      </div>
    </props.dialog>
  );
};
